/* eslint-disable react/forbid-prop-types */
/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import RenewalBanner from '@cont/Banner/RenewalBanner';
import P2 from '@base/P2/P2';
import H6 from '@base/H6/H6';
import Link from '@base/Link/Link';
import H7 from '@base/H7/H7';
import Tags from '@base/Tags/Tags';
import { useIsMobile } from '@stores/AppCommonData';
import {
  getItemRedirectURL,
  isCompletedWithNoVideoUrl,
  getInfoString,
  isLessonLive,
  isUpcoming,
  classDidNotHappen,
  isPost
} from '@models/Item';
import MoreVert from '@icon/MoreVert';
import Pin from '@icon/Pin';
import Save from '@icon/Save';
import Saved from '@icon/Saved';
import { saveEntity } from '@actions/saved';
import { segment } from '@actions/analytics';
import { SAVE_SAVE_CONTENT } from '@constants/segment';
import { topologyLevels, savedObjectTypes } from '@constants/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getSavedContentType } from '@utils/segment/propsHelper';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSnackbar } from '@stores/snackbar';
import segmentStorage from '@events/segmentStorage';
import { getIsSubscribedToLite } from '@selectors/subscription';
import { getAuthorName } from '@models/Batch';
import { NEOLESSON } from '@utils/constants';
import { getInfoStringForBatchGroup } from '@cont/BatchGroup/utils';
import { CLASS_STATE } from '@cont/BatchGroup/constants';
import RecordedVideoBlockDialog from '@cont/Syllabus/TopicPage/chapterInfo/RecordedVideoBlockDialog';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import useConstants from '@api/hooks/useConstants';
import { getIsRCPlayAllowed } from '@api/selectors/constants';
import LeftIcon from './LeftIcon';
import ExtendSubscription from './ExtendSubscription';
import DppMoreDialog from './DppMoreDialog';

const fontLightGreyColor = 'var(--color-text-secondary)';

const CourseInfo = styled(P2)`
  margin-top: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_0B : Spacings.SPACING_2B};
  @media only screen and (max-width: 47.95em) {
    font-size: 12px;
    margin-top: ${Spacings.SPACING_1B};
  }
`;

const NotesPinContainer = styled(CourseInfo)`
  display: flex;
`;

const ItemInfo = styled.div`
  width: 464px;
  flex: 1;
  @media only screen and (max-width: 47.95em) {
    width: auto;
    order: 1;
    & > h6 {
      font-size: 14px;
    }
  }
`;

const MenuButton = styled.div`
  display: flex;
  height: 24px;
  margin-left: ${Spacings.SPACING_4B};
  position: relative;
  ${mediaQuery.uptoMobile} {
    order: 2;
  }
`;

const Flex = styled.div`
  display: flex;
  margin-top: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_0B : Spacings.SPACING_2B};
  margin-bottom: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_1B : Spacings.SPACING_0B};
  align-items: center;
  flex-flow: wrap;
  row-gap: ${Spacings.SPACING_2B};
`;

const TopicGroupWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Color = styled.div`
  height: 12px;
  width: 4px;
  background-color: ${({ color }) => color};
  margin-right: ${Spacings.SPACING_1B};
  border-radius: 2px;
`;

const TopicGroupName = styled(H7)`
  margin-right: ${Spacings.SPACING_4B};
  font-weight: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Fonts.HEAVY : ''};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const Renew = styled(RenewalBanner)`
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: 0px;
`;

const Tooltip = styled(Tags)`
  font-size: 10px;
  color: var(--color-text-primary);
  padding: ${Spacings.SPACING_1B};
  border: 1px solid var(--color-divider);
  position: absolute;
  white-space: nowrap;
  left: -6px;
  top: calc(100% + 8px);
`;

const ItemContainer = styled.div`
  padding: ${Spacings.SPACING_6B};
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  margin-bottom: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_8B : Spacings.SPACING_6B};
  box-shadow: ${boxShadowGenerator()};
  display: flex;
  :hover {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B
    })};
  }
  transition: box-shadow 0.2s ease-in-out;
  @media (max-width: 47.95em) {
    width: 100%;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
`;

const FlexContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LessonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    flex-wrap: wrap;
  }
`;

const CourseInfoContainer = styled.div`
  margin-top: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_1B : Spacings.SPACING_0B};
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
`;

const ClassBatchLive = styled.div`
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_6};
  background: var(--color-i-red);
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.EXTRA_SMALL};
  color: var(--color-i-white);
  font-weight: ${Fonts.HEAVY};
  margin-right: ${Spacings.SPACING_2B};
  line-height: ${Spacings.SPACING_3B};
  text-transform: uppercase;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LessonName = styled(H6)`
  text-decoration: ${({ $isTextStrikeout = false }) =>
    $isTextStrikeout ? 'line-through' : 'unset'};
  color: ${({ $isTextStrikeout = false }) =>
    $isTextStrikeout
      ? 'var(--color-text-secondary)'
      : 'var(--color-text-primary)'};
  font-size: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? '16px' : ''};
  ${mediaQuery.uptoMobile} {
    font-size: ${({ $isRecordedBatchLesson }) =>
      $isRecordedBatchLesson ? Fonts.H6 : ''};
  }
`;
const Wrapper = styled.div`
  grid-column: 9/25;
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    display: block;
    grid-column: span 10;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    order: 3;
    width: 100%;
    flex-direction: row;
    margin-top: ${Spacings.SPACING_3B};
  }
`;

const DppButton = styled.button`
  background-color: var(--color-base-1);
  color: var(--color-text-primary);
  border: 1px solid var(--color-i-divider);
  border-radius: ${Spacings.SPACING_1B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    flex: 1;
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
    text-align: center;
  }
`;

const isQuizPage = (href) => {
  const isBatchPage = [
    '/batch/[slug]/[batchUID]/topics/[topicUID]/courses/[conceptUID]',
    '/batch/[slug]/[batchUID]/topics/[topicUID]/courses/[conceptUID]/course/[courseUID]',
    '/batch/[slug]/[batchUID]/schedule'
  ].includes(href);

  const isCoursePage = ['/course/[slug]/[courseUID]'].includes(href);

  return isBatchPage || isCoursePage;
};

const isTestSeriesPage = (href) => href === '/test-series/[slug]/[courseUID]';

const renderNotes = (showNotesCount, notesCount = 0) => {
  if (showNotesCount) {
    return (
      <NotesPinContainer color={fontLightGreyColor}>
        <Pin size="18px" color={fontLightGreyColor} />
        {notesCount} notes
      </NotesPinContainer>
    );
  }
  return null;
};

const hasNamedTopicGroups = (groups) =>
  (groups || []).some((group) => (group?.topology || group)?.name);

const renderPrimaryTopicGroup = (showPrimaryTopicGroup, topicGroups = []) => {
  if (showPrimaryTopicGroup && topicGroups.length > 0) {
    const groups = topicGroups.map((group) => group?.topology || group);
    const primaryTopicGroup =
      groups.find(
        (currentTopicGroup) => currentTopicGroup.level === topologyLevels.GROUP
      ) || groups[0];
    if (primaryTopicGroup) {
      return (
        <span style={{ color: primaryTopicGroup.color }}>
          {primaryTopicGroup.title || primaryTopicGroup.name}
          {'  •  '}
        </span>
      );
    }
  }
  return null;
};

const LiveClassBatch = ({ lesson }) => {
  const classState = lesson?.classState;
  if (classState === CLASS_STATE.LIVE_STATE) {
    return <ClassBatchLive>Live</ClassBatchLive>;
  }
  return null;
};

const ItemCard = ({
  lesson,
  onItemClick,
  showEducatorForLesson,
  showLessonNumber,
  showQuizNumber,
  showNotesCount,
  showTopicGroups,
  showPrimaryTopicGroup,
  isTestSeries,
  forPlus,
  cardPosition,
  subscription,
  setAnchorEl,
  isSubscribed,
  isBatch,
  showMoreMenu,
  renewalData,
  className,
  showBookmark,
  showRemoveDialog,
  goalUID,
  isBatchGroup,
  showLiveBatch,
  hasDppAccess,
  isRecordedBatchLesson,
  isOnlyRecordedClassesPresent,
  label
}) => {
  const isMobile = useIsMobile();
  const { route } = useRouter();
  const anchorStyle = `
  grid-column: 9/25;
  ${mediaQuery.uptoMobile} {
    display: block;
    grid-column: span 10;
  }
`;
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const retrievedGoalUID = goalUID || lesson.goal?.uid || lesson.goalUid;
  const goalSegmentData = useGoalEventProps({ goalUID: retrievedGoalUID });
  const isLiteUser = useSelector(getIsSubscribedToLite(retrievedGoalUID));
  const canAccessDpp = Boolean(isSubscribed || hasDppAccess);
  const dispatch = useDispatch();
  const isNeoLesson = lesson.classType === NEOLESSON;
  const classState = lesson?.classState;
  const isClassCancelled = classState === CLASS_STATE.CANCELLED_STATE;
  const { open: showSnackbar } = useSnackbar();
  const isRecordedGoal = useIsRecordedGoal(retrievedGoalUID);
  const [showRecordedGoalDialog, setShowRecordedGoalDialog] = useState(false);
  const [showDppMoreDialog, setShowDppMoreDialog] = useState(false);
  const isRecordedContentPlayAllowed = useConstants(
    getIsRCPlayAllowed(retrievedGoalUID)
  );
  const handleCloseRecordedVideoDialog = () => {
    setShowRecordedGoalDialog(false);
  };
  const { isLiveStreamingEnabled = true, isLive } = lesson.properties || lesson;
  const onLessonClick = (e) => {
    segmentStorage.setItem('lps', 'test-series');
    const { as } = getItemRedirectURL(lesson, forPlus);
    const { isOffline = false } = lesson.properties || lesson;
    const isStreamingDisabled = isOffline && !isLiveStreamingEnabled;
    if (isStreamingDisabled && as == null) {
      e.preventDefault();
      return;
    }

    if (
      isRecordedGoal &&
      !isRecordedContentPlayAllowed &&
      lesson.type !== 'quiz' &&
      !e?.target?.closest('.slides') &&
      !(
        e?.target?.closest('.menuButton') ||
        e?.target?.closest('.dppButton') ||
        e?.target?.closest('.MuiPopover-root')
      )
    ) {
      e.preventDefault();
      setShowRecordedGoalDialog(true);
      return;
    }
    /**
     * preventDefault was somehow blocking the redirection of lesson (CLX interface) on test series page.
     */
    if (!isTestSeries) e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    if (e?.target?.closest('.slides')) {
      setAnchorEl(null);
      return;
    }
    if (
      e?.target?.closest('.menuButton') ||
      e?.target?.closest('.dppButton') ||
      e?.target?.closest('.MuiPopover-root')
    )
      return;
    if (e.ctrlKey || e.metaKey) {
      window.open(as, '_blank');
      return;
    }
    if (!isMobile || (lesson.type === 'post' && !forPlus)) {
      // Adding LPSS to segment from label property and deleting it on class exit
      // in case property is not deleted removing it if label is not passed
      if (label) segmentStorage.setItem('lpss', label);
      else segmentStorage.deleteItem('lpss');
      onItemClick(lesson);
    }
  };

  const handleMenuClick = (event) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget, lesson);
  };
  const handleDppClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoggedIn) {
      openLoginDrawer();
      return;
    }
    setShowDppMoreDialog(true);
  };
  const renderTopicGroups = () => {
    const topicGroupTextColor = isClassCancelled
      ? 'var(--color-text-secondary)'
      : 'var(--color-text-primary)';

    const showTags = isMobile ? 1 : 3;

    return (
      <Flex $isRecordedBatchLesson={isRecordedBatchLesson}>
        {lesson.topicGroups.slice(0, showTags).map((topicGroup) => {
          const tagColor = isClassCancelled
            ? 'var(--color-text-secondary)'
            : topicGroup.color;
          return (
            <TopicGroupWrapper key={topicGroup.uid}>
              {!isRecordedBatchLesson && <Color color={tagColor} />}
              <TopicGroupName
                key={`${lesson.uid}_${topicGroup.uid}`}
                color={
                  isRecordedBatchLesson ? topicGroup.color : topicGroupTextColor
                }
                as="h5"
                $isRecordedBatchLesson={isRecordedBatchLesson}
              >
                {isRecordedBatchLesson
                  ? topicGroup.name.toUpperCase()
                  : topicGroup.name}
              </TopicGroupName>
            </TopicGroupWrapper>
          );
        })}
      </Flex>
    );
  };
  const renderExtendCard = () => {
    if (renewalData?.show)
      return (
        <Renew
          data={renewalData}
          subscription={subscription}
          gridColumn="9/25"
          lastPrimarySource="Course Page"
        />
      );
    return <ExtendSubscription subscription={subscription} />;
  };

  const { as, href } = getItemRedirectURL(lesson, forPlus);

  const isItemCompletedWithNoVideoUrl = isCompletedWithNoVideoUrl(lesson);
  const hideUploadedDate = isOnlyRecordedClassesPresent || isNeoLesson;

  const infoString = isBatchGroup
    ? getInfoStringForBatchGroup(lesson)
    : getInfoString(
        lesson,
        isTestSeries,
        forPlus,
        isBatch,
        false,
        showLessonNumber,
        showQuizNumber,
        1,
        isRecordedBatchLesson,
        hideUploadedDate
      );
  const strForLoggedInUser =
    isItemCompletedWithNoVideoUrl && !isLiteUser
      ? 'Lesson recording will be available soon'
      : infoString;
  const displayInfoString = !isSubscribed ? infoString : strForLoggedInUser;
  const addToCollection = () => {
    const isItemPost = isPost(lesson);
    const entityType = isItemPost
      ? savedObjectTypes.LESSON
      : savedObjectTypes.TEST;
    dispatch(
      saveEntity({
        goalUid: goalUID || lesson.goal?.uid || lesson.goalUid,
        objectUid: lesson.uid,
        entityType
      })
    ).then(() => {
      showSnackbar({
        message: `Saved to ${isItemPost ? 'Lessons' : 'Tests'}`,
        type: 'success'
      });
      segment.track(SAVE_SAVE_CONTENT, {
        save_content_type: getSavedContentType(entityType),
        save_content_uid: lesson.uid,
        save_content_title: lesson.name,
        ...goalSegmentData
      });
    });
  };

  const isSaved = useSelector(({ items }) =>
    items.data?.[lesson.uid]?.isSaved !== undefined
      ? items.data?.[lesson.uid]?.isSaved
      : lesson.isSaved
  );
  const isQuiz = lesson?.type === 'quiz' && isQuizPage(route);
  const isLessonInTestSeries =
    lesson?.type === 'post' && isTestSeriesPage(route);
  const enableBookmark =
    !isQuiz &&
    !isLessonInTestSeries &&
    !isLessonLive(lesson) &&
    isSubscribed &&
    showBookmark &&
    !isUpcoming(lesson) &&
    !classDidNotHappen(lesson) &&
    !isItemCompletedWithNoVideoUrl;

  const dialogLesson = {
    ...lesson,
    topicGroups: hasNamedTopicGroups(lesson.topicGroups)
      ? lesson.topicGroups
      : []
  };

  const [showTooltip, setShowTooltip] = useState(false);

  const onSaveClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isSaved) {
      showRemoveDialog(lesson);
    } else {
      addToCollection();
    }
    setShowTooltip(false);
  };
  const renderItem = () => {
    return (
      <ItemContainer
        className="itemContainer"
        isRecordedBatchLesson={isRecordedBatchLesson}
      >
        <LeftIcon
          isBatchGroup={isBatchGroup}
          lesson={lesson}
          isRecordedBatchLesson={isRecordedBatchLesson}
        />
        <FlexContent>
          <LessonWrapper className="lessonWrapper">
            <ItemInfo id={lesson.uid}>
              {isRecordedBatchLesson &&
                showTopicGroups &&
                isBatch &&
                isPost(lesson) &&
                renderTopicGroups()}
              <LessonName
                $isTextStrikeout={isClassCancelled}
                color="var(--color-text-primary)"
                as="h3"
                $isRecordedBatchLesson={isRecordedBatchLesson}
              >
                {lesson.name || lesson?.properties?.title}
              </LessonName>
              <CourseInfoContainer
                $isRecordedBatchLesson={isRecordedBatchLesson}
              >
                {showLiveBatch && <LiveClassBatch lesson={lesson} />}
                <CourseInfo
                  color="var(--color-text-secondary)"
                  $isRecordedBatchLesson={isRecordedBatchLesson}
                >
                  {renderPrimaryTopicGroup(
                    showPrimaryTopicGroup,
                    lesson?.topicGroups
                  )}
                  {displayInfoString}
                  {showEducatorForLesson &&
                    `  •  ${getAuthorName(lesson?.author || {})}`}
                </CourseInfo>
              </CourseInfoContainer>
              {renderNotes(showNotesCount, lesson?.notesCount)}
              {!isRecordedBatchLesson &&
                showTopicGroups &&
                isBatch &&
                renderTopicGroups()}
            </ItemInfo>
            {lesson.type !== 'quiz' &&
              !classDidNotHappen(lesson) &&
              !isLessonLive(lesson) &&
              (isBatch || !isUpcoming(lesson)) && (
                <ActionContainer>
                  <DppButton
                    type="button"
                    className="dppButton"
                    onClick={handleDppClick}
                  >
                    DPP & more
                  </DppButton>
                </ActionContainer>
              )}
            {enableBookmark && (
              <MenuButton
                onClick={onSaveClick}
                onMouseOver={isSaved ? undefined : () => setShowTooltip(true)}
                onMouseOut={isSaved ? undefined : () => setShowTooltip(false)}
              >
                {isSaved ? (
                  <Saved
                    size={Spacings.SPACING_6B}
                    color="var(--color-i-green)"
                  />
                ) : (
                  <Save
                    size={Spacings.SPACING_6B}
                    color="var(--color-text-primary)"
                  />
                )}
                {showTooltip && !isSaved && <Tooltip>Save</Tooltip>}
              </MenuButton>
            )}
            {showMoreMenu && !isRecordedBatchLesson && (
              <MenuButton onClick={handleMenuClick} className="menuButton">
                <MoreVert size="24px" color="var(--color-text-primary)" />
              </MenuButton>
            )}
          </LessonWrapper>
        </FlexContent>
      </ItemContainer>
    );
  };
  if (isLoggedIn && isLive && !isLiveStreamingEnabled) {
    return (
      <>
        <Wrapper className={className}>{renderItem()}</Wrapper>
        {cardPosition === lesson.rank && renderExtendCard()}
        <RecordedVideoBlockDialog
          isOpen={showRecordedGoalDialog}
          onClose={handleCloseRecordedVideoDialog}
        />
        <DppMoreDialog
          showDialog={showDppMoreDialog}
          onClose={() => setShowDppMoreDialog(false)}
          lesson={dialogLesson}
          goalUID={retrievedGoalUID}
          source={isBatch ? 'Batch' : 'Course'}
          isDisabled={!canAccessDpp || (isBatch && isUpcoming(lesson))}
          isLockedForPurchase={!canAccessDpp}
        />
      </>
    );
  }
  return (
    <>
      {isLoggedIn ? (
        <Link
          href={href}
          as={as}
          anchorStyle={anchorStyle}
          className={className}
          onClick={onLessonClick}
        >
          {renderItem()}
        </Link>
      ) : (
        <Wrapper onClick={onLessonClick} className={className}>
          {renderItem()}
        </Wrapper>
      )}
      {cardPosition === lesson.rank && renderExtendCard()}
      <RecordedVideoBlockDialog
        isOpen={showRecordedGoalDialog}
        onClose={handleCloseRecordedVideoDialog}
      />
      <DppMoreDialog
        showDialog={showDppMoreDialog}
        onClose={() => setShowDppMoreDialog(false)}
        lesson={dialogLesson}
        goalUID={retrievedGoalUID}
        source={isBatch ? 'Batch' : 'Course'}
        isDisabled={!canAccessDpp || (isBatch && isUpcoming(lesson))}
        isLockedForPurchase={!canAccessDpp}
      />
    </>
  );
};

export default ItemCard;

ItemCard.propTypes = {
  lesson: PropTypes.objectOf(PropTypes.any).isRequired,
  onItemClick: PropTypes.func.isRequired,
  showEducatorForLesson: PropTypes.bool.isRequired,
  showNotesCount: PropTypes.bool,
  showLessonNumber: PropTypes.bool,
  showTopicGroups: PropTypes.bool,
  showPrimaryTopicGroup: PropTypes.bool,
  showQuizNumber: PropTypes.bool,
  isTestSeries: PropTypes.bool,
  forPlus: PropTypes.bool.isRequired,
  cardPosition: PropTypes.number.isRequired,
  subscription: PropTypes.objectOf(PropTypes.any),
  setAnchorEl: PropTypes.func.isRequired,
  isSubscribed: PropTypes.bool,
  isBatch: PropTypes.bool.isRequired,
  showMoreMenu: PropTypes.bool,
  className: PropTypes.string,
  showBookmark: PropTypes.bool,
  showRemoveDialog: PropTypes.func,
  isBatchGroup: PropTypes.bool,
  label: PropTypes.string
};

ItemCard.defaultProps = {
  showLessonNumber: true,
  showQuizNumber: true,
  showNotesCount: false,
  showPrimaryTopicGroup: false,
  showTopicGroups: true,
  isTestSeries: false,
  subscription: null,
  isSubscribed: false,
  className: '',
  showMoreMenu: false,
  showBookmark: true,
  showRemoveDialog: () => {},
  isBatchGroup: false,
  label: ''
};
